export type WorkspaceTimedMediaMimeType =
  | 'video/mp4'
  | 'video/quicktime'
  | 'audio/mpeg'
  | 'audio/wav';

const MIME_TYPE_ALIASES: Readonly<Record<string, WorkspaceTimedMediaMimeType>> =
  {
    'video/mp4': 'video/mp4',
    'application/mp4': 'video/mp4',
    'video/quicktime': 'video/quicktime',
    'video/x-quicktime': 'video/quicktime',
    'audio/mpeg': 'audio/mpeg',
    'audio/mp3': 'audio/mpeg',
    'audio/x-mpeg': 'audio/mpeg',
    'audio/x-mp3': 'audio/mpeg',
    'audio/wav': 'audio/wav',
    'audio/x-wav': 'audio/wav',
    'audio/wave': 'audio/wav',
    'audio/vnd.wave': 'audio/wav',
  };

const MIME_TYPE_BY_EXTENSION: Readonly<
  Record<string, WorkspaceTimedMediaMimeType>
> = {
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
};

const ACCEPT_TOKENS_BY_MIME: Readonly<
  Record<WorkspaceTimedMediaMimeType, readonly string[]>
> = {
  'video/mp4': ['video/mp4', 'application/mp4', '.mp4'],
  'video/quicktime': ['video/quicktime', 'video/x-quicktime', '.mov'],
  'audio/mpeg': [
    'audio/mpeg',
    'audio/mp3',
    'audio/x-mpeg',
    'audio/x-mp3',
    '.mp3',
  ],
  'audio/wav': [
    'audio/wav',
    'audio/x-wav',
    'audio/wave',
    'audio/vnd.wave',
    '.wav',
  ],
};

export const WORKSPACE_TIMED_MEDIA_UPLOAD_MIME_TYPES = Object.freeze(
  Object.keys(MIME_TYPE_ALIASES)
);

function normalizeMimeType(value: string) {
  return value.split(';')[0]?.trim().toLowerCase() ?? '';
}

function readExtension(fileName: string) {
  const normalized = fileName.trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf('.');
  return dotIndex >= 0 && dotIndex < normalized.length - 1
    ? normalized.slice(dotIndex + 1)
    : '';
}

const COMMON_VIDEO_EXTENSIONS = new Set([
  'mp4',
  'mov',
  'm4v',
  'webm',
  'mkv',
  'avi',
]);
const COMMON_AUDIO_EXTENSIONS = new Set([
  'mp3',
  'wav',
  'm4a',
  'aac',
  'ogg',
  'flac',
]);

export function inferWorkspaceTimedMediaKind(file: {
  name: string;
  type: string;
}): 'video' | 'audio' | null {
  const mimeType = normalizeMimeType(file.type);
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  const extension = readExtension(file.name);
  if (COMMON_VIDEO_EXTENSIONS.has(extension)) return 'video';
  if (COMMON_AUDIO_EXTENSIONS.has(extension)) return 'audio';
  return null;
}

export function normalizeWorkspaceTimedMediaMimeType(
  value: string
): WorkspaceTimedMediaMimeType | null {
  return MIME_TYPE_ALIASES[normalizeMimeType(value)] ?? null;
}

export function resolveWorkspaceTimedMediaFileMimeType(file: {
  name: string;
  type: string;
}): WorkspaceTimedMediaMimeType | null {
  return (
    normalizeWorkspaceTimedMediaMimeType(file.type) ??
    MIME_TYPE_BY_EXTENSION[readExtension(file.name)] ??
    null
  );
}

export function resolveWorkspaceTimedMediaAcceptTokens(
  supportedMimeTypes?: readonly string[]
) {
  if (!supportedMimeTypes?.length) {
    return Array.from(new Set(Object.values(ACCEPT_TOKENS_BY_MIME).flat()));
  }

  const canonicalMimeTypes = supportedMimeTypes
    .map(normalizeWorkspaceTimedMediaMimeType)
    .filter((value): value is WorkspaceTimedMediaMimeType => value !== null);

  return Array.from(
    new Set(
      canonicalMimeTypes.flatMap((mimeType) => ACCEPT_TOKENS_BY_MIME[mimeType])
    )
  );
}

export function isWorkspaceTimedMediaFileAccepted(params: {
  file: { name: string; type: string };
  supportedMimeTypes?: readonly string[];
}) {
  const resolved = resolveWorkspaceTimedMediaFileMimeType(params.file);
  if (!resolved) return false;
  if (!params.supportedMimeTypes?.length) return true;
  const supported = new Set(
    params.supportedMimeTypes
      .map(normalizeWorkspaceTimedMediaMimeType)
      .filter((value): value is WorkspaceTimedMediaMimeType => value !== null)
  );
  return supported.has(resolved);
}

export function isWorkspaceTimedMediaMimeType(value: string) {
  return normalizeWorkspaceTimedMediaMimeType(value) !== null;
}
